import { useMemo, useState } from 'react';
import { Alert, Box, Snackbar, Typography } from '@mui/material';
import { useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { metadataApi } from '@/api/modules/metadata';
import { dynamicRecordsApi } from '@/api/modules/dynamicRecords';
import { newIdempotencyKey } from '@/api/client';
import { ApiException, type ApiError } from '@/api/types';
import { DynamicForm } from '@/forms/DynamicForm';
import type { FieldDefinition, FormValues } from '@/forms/types';

/**
 * End-to-end demo of the metadata-driven form pipeline:
 *   1. Fetch field definitions for an entity definition id
 *   2. Hand them to {@link DynamicForm} for rendering + value collection
 *   3. POST the resulting payload to the backend with an idempotency key
 *   4. Map any 400 VALIDATION_FAILED back onto per-field errors
 */
export function RecordSubmitPage() {
  const { entityDefinitionId } = useParams<{ entityDefinitionId: string }>();
  const [serverError, setServerError] = useState<ApiError | null>(null);
  const [savedId, setSavedId] = useState<string | null>(null);
  // Keep one idempotency key per page mount so a user double-tap-submit can't
  // create two records.
  const idempotencyKey = useMemo(() => newIdempotencyKey(), []);

  const fieldsQuery = useQuery({
    queryKey: ['metadata', 'fields', entityDefinitionId],
    queryFn: () => metadataApi.listFields(entityDefinitionId!),
    enabled: Boolean(entityDefinitionId)
  });

  if (!entityDefinitionId) {
    return (
      <Box>
        <Typography variant="h1" gutterBottom>
          Dynamic record submission
        </Typography>
        <Alert severity="info">
          Pick an entity definition to start. URL pattern:
          <code> /dynamic-records/&lt;entityDefinitionId&gt;/new</code>.
        </Alert>
      </Box>
    );
  }

  const submit = async (values: FormValues) => {
    setServerError(null);
    try {
      const created = await dynamicRecordsApi.create(
        entityDefinitionId,
        values as Record<string, unknown>,
        idempotencyKey
      );
      setSavedId(created.id);
    } catch (e) {
      if (e instanceof ApiException) setServerError(e.error);
      else throw e;
    }
  };

  const fieldDefinitions: FieldDefinition[] =
    fieldsQuery.data?.map((f) => ({
      name: f.name,
      label: f.name,
      fieldType: f.fieldType,
      required: f.required,
      displayOrder: f.displayOrder
    })) ?? [];

  return (
    <Box sx={{ maxWidth: 720 }}>
      <Typography variant="h1" gutterBottom>
        Submit dynamic record
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Entity definition: <code>{entityDefinitionId}</code>
      </Typography>

      {fieldsQuery.isLoading && <Typography>Loading fields…</Typography>}
      {fieldsQuery.isError && (
        <Alert severity="error">Failed to load field definitions.</Alert>
      )}
      {fieldsQuery.data && (
        <DynamicForm
          fields={fieldDefinitions}
          onSubmit={submit}
          submitLabel="Submit"
          error={serverError}
        />
      )}

      <Snackbar
        open={Boolean(savedId)}
        autoHideDuration={4000}
        onClose={() => setSavedId(null)}
        message={`Record saved (id: ${savedId?.slice(0, 8)}…)`}
      />
    </Box>
  );
}
